import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import { Box, CircularProgress, Divider, Typography } from "@mui/material";
import { ReactNode, useState } from "react";
import { MealType } from "../../../../types";
import { getHueForMealMenu } from "../../CalendarEvent/color";
import { CurrentSharingStatus, SharingRecipientSummary, isSharingSummaryEmpty, sharingSelectionsDiffer } from "../sharingSummary";

export interface CurrentMenuSharing {
  id: string;
  name: string;
  mealType: MealType;
  scheduleLabel: string;
  summary: SharingRecipientSummary;
  identityKey: string;
  status: CurrentSharingStatus;
}

const cardSx = {
  bgcolor: "grey.50",
  borderRadius: 2,
  border: 1,
  borderColor: "grey.200",
  width: "100%",
  minWidth: 0,
} as const;

const toggleButtonSx = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: 1,
  px: 2,
  py: 1.5,
  border: 0,
  bgcolor: "transparent",
  color: "inherit",
  font: "inherit",
  textAlign: "left",
  cursor: "pointer",
  "&:focus-visible": {
    outline: "2px solid",
    outlineColor: "primary.main",
    outlineOffset: -2,
  },
} as const;

const swatchColor = (mealType: MealType) => `hsl(${getHueForMealMenu(mealType, 0)}, 70%, 62%)`;

const excludedNames = (summary: SharingRecipientSummary) => [
  ...summary.excludedTeams,
  ...summary.excludedPositions,
  ...summary.excludedAthletes,
  ...summary.excludedTags,
];

const SharingRow = ({ icon, label, value }: { icon: ReactNode; label: string; value: string }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: "120px minmax(0, 1fr)", columnGap: 2, alignItems: "start" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary" }}>
      {icon}
      <Typography variant="body2">{label}</Typography>
    </Box>
    <Typography variant="body2" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
      {value}
    </Typography>
  </Box>
);

const SharingRows = ({ summary }: { summary: SharingRecipientSummary }) => {
  if (isSharingSummaryEmpty(summary)) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Not shared with anyone
      </Typography>
    );
  }

  const excluded = excludedNames(summary);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1.5 }}>
      {summary.teams.length > 0 && (
        <SharingRow icon={<GroupsOutlinedIcon fontSize="small" />} label="Teams" value={summary.teams.join(", ")} />
      )}
      {summary.positions.length > 0 && (
        <SharingRow icon={<BadgeOutlinedIcon fontSize="small" />} label="Positions" value={summary.positions.join(", ")} />
      )}
      {summary.athletes.length > 0 && (
        <SharingRow icon={<PersonOutlinedIcon fontSize="small" />} label="Athletes" value={summary.athletes.join(", ")} />
      )}
      {summary.tags.length > 0 && (
        <SharingRow icon={<LocalOfferOutlinedIcon fontSize="small" />} label="Tags" value={summary.tags.join(", ")} />
      )}
      {excluded.length > 0 && <SharingRow icon={<BlockOutlinedIcon fontSize="small" />} label="Excluded" value={excluded.join(", ")} />}
    </Box>
  );
};

const SharingDetails = ({ menu }: { menu: CurrentMenuSharing }) => {
  if (menu.status === "loading") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
        <CircularProgress size={16} aria-label="Loading current sharing" />
        <Typography variant="body2" color="text.secondary">
          Loading current sharing
        </Typography>
      </Box>
    );
  }
  if (menu.status === "error") {
    return (
      <Typography variant="body2" color="error" sx={{ py: 1 }}>
        Couldn&apos;t load current sharing
      </Typography>
    );
  }
  return <SharingRows summary={menu.summary} />;
};

const MenuIdentity = ({ menu }: { menu: CurrentMenuSharing }) => (
  <Box sx={{ display: "flex", gap: 1.5, minWidth: 0 }}>
    <Box aria-hidden sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: swatchColor(menu.mealType), mt: 0.7, flexShrink: 0 }} />
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body1" fontWeight={600} sx={{ overflowWrap: "anywhere" }}>
        {menu.name}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {menu.scheduleLabel}
      </Typography>
    </Box>
  </Box>
);

const SingleMenuSharing = ({ menu }: { menu: CurrentMenuSharing }) => (
  <>
    <Box sx={{ ...cardSx, px: 2, py: 1.5 }}>
      <MenuIdentity menu={menu} />
    </Box>
    <Box>
      <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
        Shared with
      </Typography>
      <SharingDetails menu={menu} />
    </Box>
  </>
);

const MenuSharingToggle = ({ menu }: { menu: CurrentMenuSharing }) => {
  const [expanded, setExpanded] = useState(false);
  const showNotShared = !expanded && menu.status === "ready" && isSharingSummaryEmpty(menu.summary);

  return (
    <Box>
      <Box component="button" type="button" aria-expanded={expanded} onClick={() => setExpanded(open => !open)} sx={toggleButtonSx}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <MenuIdentity menu={menu} />
        </Box>
        {showNotShared && (
          <Typography variant="body2" color="text.secondary">
            Not shared
          </Typography>
        )}
        <ExpandMoreIcon
          fontSize="small"
          sx={{ color: "text.secondary", transform: expanded ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform 150ms" }}
        />
      </Box>
      {expanded && (
        <Box sx={{ px: 2, pb: 1.5, pl: 5.5, minWidth: 0 }}>
          <SharingDetails menu={menu} />
        </Box>
      )}
    </Box>
  );
};

const MultiMenuSharing = ({ menus }: { menus: readonly CurrentMenuSharing[] }) => {
  const [expanded, setExpanded] = useState(false);
  const differs = sharingSelectionsDiffer(menus);

  return (
    <Box sx={cardSx}>
      <Box component="button" type="button" aria-expanded={expanded} onClick={() => setExpanded(open => !open)} sx={toggleButtonSx}>
        <ExpandMoreIcon
          fontSize="small"
          sx={{ color: "text.secondary", transform: expanded ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform 150ms" }}
        />
        <Box>
          <Typography variant="body1" fontWeight={600}>
            Currently shared with
          </Typography>
          {differs && (
            <Typography variant="body2" color="text.secondary">
              Sharing differs across these menus
            </Typography>
          )}
        </Box>
      </Box>
      {expanded && (
        <Box>
          <Divider />
          {menus.map((menu, index) => (
            <Box key={menu.id}>
              <MenuSharingToggle menu={menu} />
              {index < menus.length - 1 && <Divider />}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const CurrentSharing = ({ menus }: { menus: readonly CurrentMenuSharing[] }) => {
  if (menus.length === 0) {
    return null;
  }
  const onlyMenu = menus.length === 1 ? menus[0] : null;
  if (onlyMenu) {
    return <SingleMenuSharing menu={onlyMenu} />;
  }
  return <MultiMenuSharing menus={menus} />;
};

export default CurrentSharing;
